import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiServer, FiRefreshCw, FiAlertCircle, FiCheckCircle,
  FiZap, FiPlusCircle, FiEdit2, FiTrash2, FiToggleLeft, FiToggleRight,
  FiPlay, FiSettings, FiSave, FiEye, FiEyeOff, FiCheck, FiX,
  FiExternalLink, FiWifi, FiWifiOff, FiClock, FiAlertTriangle,
} from 'react-icons/fi';
import {
  getAdminAdoConfig,
  updateAdminAdoConfig,
  testAdminAdoConfig,
  getFactoryProjects,
  createFactoryProject,
  updateFactoryProject,
  deleteFactoryProject,
  bootstrapAdoOrg,
  streamBootstrapFactoryProject,
  streamBootstrapEnterprisePipeline,
} from '../../api/admin';
import type { AdoConfigRow, FactoryProject, BootstrapProgressEvent, BootstrapEnterprisePipelineEvent } from '../../api/admin';

const PLANS = ['FREE', 'STANDARD', 'ENTREPRISE', 'BUSINESS'] as const;
type PlanCode = typeof PLANS[number];

// ── Badges ───────────────────────────────────────────────────────────────────

const PlanBadge: React.FC<{ plan: string }> = ({ plan }) => {
  const cls =
    plan === 'FREE'       ? 'bg-gray-100 text-gray-600' :
    plan === 'STANDARD'   ? 'bg-blue-100 text-blue-700' :
    plan === 'ENTREPRISE' ? 'bg-purple-100 text-purple-700' :
                            'bg-orange-100 text-orange-700';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{plan}</span>;
};

// ── Modal Factory Project ────────────────────────────────────────────────────

interface FPModalProps {
  planCode: PlanCode;
  initial?: Partial<FactoryProject>;
  onClose: () => void;
  onSave: () => void;
}

const FactoryProjectModal: React.FC<FPModalProps> = ({ planCode, initial, onClose, onSave }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    name:            initial?.name                ?? '',
    ado_project:     initial?.ado_project         ?? 'terraform-executions',
    pipeline_id:     initial?.pipeline_id?.toString()     ?? '',
    pipeline_id_aws: initial?.pipeline_id_aws?.toString() ?? '',
    pipeline_id_gcp: initial?.pipeline_id_gcp?.toString() ?? '',
    max_concurrency: initial?.max_concurrency?.toString()  ?? '3',
  });
  const [saving,          setSaving]          = useState(false);
  const [error,           setError]           = useState<string | null>(null);
  const [bootstrapResult, setBootstrapResult] = useState<{ project?: string | null; agentPool?: string | null; pipeline?: number | null; yamlPushed?: boolean; yamlWarning?: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (isEdit && initial?.id) {
        await updateFactoryProject(initial.id, {
          ado_project:     form.ado_project.trim(),
          pipeline_id:     form.pipeline_id     ? parseInt(form.pipeline_id)     : null,
          pipeline_id_aws: form.pipeline_id_aws ? parseInt(form.pipeline_id_aws) : null,
          pipeline_id_gcp: form.pipeline_id_gcp ? parseInt(form.pipeline_id_gcp) : null,
          max_concurrency: parseInt(form.max_concurrency) || 3,
        });
        onSave();
      } else {
        const res = await createFactoryProject({
          name:            form.name.trim(),
          plan_code:       planCode,
          max_concurrency: parseInt(form.max_concurrency) || 3,
        });
        setBootstrapResult(res.bootstrap);
      }
    } catch (err: unknown) {
      const ae = err as { response?: { data?: { error?: string } }; message?: string };
      setError(ae.response?.data?.error ?? ae.message ?? 'Erreur inconnue');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            {isEdit ? 'Modifier le Factory Project' : 'Nouveau Factory Project'}
            <PlanBadge plan={planCode} />
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        {bootstrapResult ? (
          <div className="px-6 py-6 space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="font-semibold text-green-800 flex items-center gap-2 mb-3">
                <FiCheckCircle size={16} /> Factory project créé et bootstrappé
              </p>
              <ul className="text-sm text-green-700 space-y-1">
                {bootstrapResult.project   && <li>✅ Projet ADO : <span className="font-mono">{bootstrapResult.project}</span></li>}
                {bootstrapResult.agentPool && <li>✅ Agent Pool : <span className="font-mono">{bootstrapResult.agentPool}</span></li>}
                {bootstrapResult.pipeline  && <li>✅ Pipeline ID : <span className="font-mono">{bootstrapResult.pipeline}</span></li>}
                {bootstrapResult.yamlPushed === true  && <li>✅ Pipeline YAML poussé dans le repo ADO</li>}
                {bootstrapResult.yamlPushed === false && <li>⚠️ Pipeline YAML non poussé — PAT nécessite <span className="font-mono">Code: read &amp; write</span></li>}
              </ul>
            </div>
            {bootstrapResult.yamlWarning && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                <span className="font-semibold">Action requise :</span> {bootstrapResult.yamlWarning}
              </div>
            )}
            <div className="flex justify-end">
              <button onClick={onSave}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition"
              >Fermer</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

            {!isEdit && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-xs flex items-start gap-2">
                <FiPlay size={12} className="mt-0.5 flex-shrink-0" />
                <span>Le projet ADO, l'agent pool et le pipeline seront créés automatiquement en utilisant le PAT de la Configuration ADO.</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="ex: free-factory-2"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                required
                disabled={isEdit}
              />
              {isEdit && <p className="text-xs text-gray-400 mt-1">Le nom ne peut pas être modifié après création.</p>}
            </div>

            {isEdit && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Projet ADO</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={form.ado_project}
                    onChange={e => setForm(f => ({ ...f, ado_project: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pipeline ID — Azure <span className="text-gray-400">(optionnel)</span>
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Hérite de la config ADO"
                    value={form.pipeline_id}
                    onChange={e => setForm(f => ({ ...f, pipeline_id: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pipeline ID — AWS <span className="text-gray-400">(optionnel)</span>
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="ID du pipeline azure-pipelines-terraform-aws.yml"
                    value={form.pipeline_id_aws}
                    onChange={e => setForm(f => ({ ...f, pipeline_id_aws: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pipeline ID — GCP <span className="text-gray-400">(optionnel)</span>
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="ID du pipeline azure-pipelines-terraform-gcp.yml"
                    value={form.pipeline_id_gcp}
                    onChange={e => setForm(f => ({ ...f, pipeline_id_gcp: e.target.value }))}
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Concurrence max</label>
              <input
                type="number"
                min={1}
                max={20}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={form.max_concurrency}
                onChange={e => setForm(f => ({ ...f, max_concurrency: e.target.value }))}
                required
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition"
              >Annuler</button>
              <button type="submit" disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 transition"
              >
                {saving
                  ? (isEdit ? 'Enregistrement…' : <><FiRefreshCw size={12} className="animate-spin" /> Bootstrap en cours…</>)
                  : (isEdit ? 'Modifier' : <><FiPlay size={12} /> Créer & Bootstrapper</>)}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ── Modal Bootstrap ──────────────────────────────────────────────────────────

interface BootstrapModalProps {
  planCode: PlanCode;
  onClose: () => void;
  onSuccess: () => void;
}

const BootstrapModal: React.FC<BootstrapModalProps> = ({ planCode, onClose, onSuccess }) => {
  const [form, setForm] = useState({ ado_org_url: '', pat: '', repo_url: '', signing_key: '' });
  const [running, setRunning] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [result,  setResult]  = useState<Record<string, unknown> | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRunning(true);
    setError(null);
    try {
      const res = await bootstrapAdoOrg({
        plan_code:   planCode,
        ado_org_url: form.ado_org_url.trim(),
        pat:         form.pat.trim(),
        repo_url:    form.repo_url.trim()    || undefined,
        signing_key: form.signing_key.trim() || undefined,
      });
      setResult(res.result as Record<string, unknown>);
      onSuccess();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Bootstrap échoué');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4">
        <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FiPlay className="text-indigo-600" size={16} />
            Bootstrap Organisation ADO
            <PlanBadge plan={planCode} />
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        {result ? (
          <div className="px-6 py-6 space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="font-semibold text-green-800 flex items-center gap-2 mb-3">
                <FiCheckCircle size={16} /> Bootstrap terminé avec succès
              </p>
              <ul className="text-sm text-green-700 space-y-1">
                {Object.entries(result)
                  .filter(([, v]) => Boolean(v))
                  .map(([k, v]) => (
                    <li key={k}><span className="font-medium">{k}:</span> {String(v)}</li>
                  ))}
              </ul>
            </div>
            <div className="flex justify-end">
              <button onClick={onClose}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition"
              >Fermer</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2">
                <FiAlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-xs">
              PAT requis : <strong>Project (R/W) · Build (R/W) · Agent Pools (R/W) · Variable Groups (R/W)</strong>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL Organisation ADO</label>
              <input type="url"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder={`https://dev.azure.com/GenIaC-${planCode.charAt(0) + planCode.slice(1).toLowerCase()}`}
                value={form.ado_org_url}
                onChange={e => setForm(f => ({ ...f, ado_org_url: e.target.value }))}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Personal Access Token (PAT)</label>
              <input type="password"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="••••••••••••••••••••••••••••••••••"
                value={form.pat}
                onChange={e => setForm(f => ({ ...f, pat: e.target.value }))}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                URL Repo Git <span className="text-gray-400">(optionnel — pour créer le pipeline)</span>
              </label>
              <input type="url"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="https://github.com/geniac/geniac-app"
                value={form.repo_url}
                onChange={e => setForm(f => ({ ...f, repo_url: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Clé publique Ed25519 <span className="text-gray-400">(optionnel)</span>
              </label>
              <textarea rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="ssh-ed25519 AAAA..."
                value={form.signing_key}
                onChange={e => setForm(f => ({ ...f, signing_key: e.target.value }))}
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition"
              >Annuler</button>
              <button type="submit" disabled={running}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 transition flex items-center gap-2"
              >
                {running
                  ? <><FiRefreshCw size={13} className="animate-spin" /> Bootstrap en cours…</>
                  : <><FiPlay size={13} /> Lancer le Bootstrap</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ── Section Config ADO ───────────────────────────────────────────────────────

interface ConfigSectionProps {
  config:      AdoConfigRow | undefined;
  planCode:    PlanCode;
  onSaved:     (updated: AdoConfigRow) => void;
  onBootstrap: () => void;
}

/* ── PAT expiry helpers ── */
const patDaysLeft = (expiresAt: string | null): number | null => {
  if (!expiresAt) return null;
  return Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86_400_000);
};

const PatExpiryBadge: React.FC<{ expiresAt: string | null }> = ({ expiresAt }) => {
  const days = patDaysLeft(expiresAt);
  if (days === null) return null;
  if (days <= 0)  return <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">PAT expiré</span>;
  if (days <= 7)  return <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">PAT expire dans {days}j</span>;
  if (days <= 30) return <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-600 font-medium">PAT expire dans {days}j</span>;
  return <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-600">PAT valide {days}j</span>;
};

const ConfigSection: React.FC<ConfigSectionProps> = ({ config, planCode, onSaved, onBootstrap }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    organization:   config?.organization  ?? '',
    base_url:       config?.base_url      ?? 'https://dev.azure.com',
    pipeline_id:    String(config?.pipeline_id ?? 1),
    project_name:   config?.project_name  ?? 'terraform-executions',
    pat:            '',
    pat_expires_at: config?.pat_expires_at ? config.pat_expires_at.slice(0, 10) : '',
    is_active:      config?.is_active     ?? false,
    on_premise:     config?.base_url ? !config.base_url.includes('dev.azure.com') : false,
  });
  const [showPat,  setShowPat]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [testing,  setTesting]  = useState(false);
  const [success,  setSuccess]  = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Bootstrap Enterprise/Business Pipeline (SSE)
  const [entBootstrapOpen, setEntBootstrapOpen] = useState(false);
  const [entBootstrapProgress, setEntBootstrapProgress] = useState<Map<string, BootstrapEnterprisePipelineEvent>>(new Map());
  const [entBootstrapDone, setEntBootstrapDone] = useState(false);
  const entBootstrapCleanupRef = useRef<(() => void) | null>(null);

  const handleEntBootstrap = () => {
    if (!config?.has_pat) return;
    setEntBootstrapOpen(true);
    setEntBootstrapProgress(new Map());
    setEntBootstrapDone(false);
    const cleanup = streamBootstrapEnterprisePipeline(
      planCode as 'ENTREPRISE' | 'BUSINESS',
      (event) => {
        setEntBootstrapProgress(prev => new Map(prev).set(event.step, event));
        if (event.step === 'done' || event.step === 'error') setEntBootstrapDone(true);
      }
    );
    entBootstrapCleanupRef.current = cleanup;
  };

  const handleCloseEntBootstrap = () => {
    entBootstrapCleanupRef.current?.();
    entBootstrapCleanupRef.current = null;
    setEntBootstrapOpen(false);
    setEntBootstrapProgress(new Map());
    setEntBootstrapDone(false);
  };

  useEffect(() => {
    if (config) {
      setForm({
        organization:   config.organization,
        base_url:       config.base_url,
        pipeline_id:    String(config.pipeline_id),
        project_name:   config.project_name,
        pat:            '',
        pat_expires_at: config.pat_expires_at ? config.pat_expires_at.slice(0, 10) : '',
        is_active:      config.is_active,
        on_premise:     !config.base_url.includes('dev.azure.com'),
      });
      setTestResult(null);
    }
  }, [planCode]);

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const adoUrl = `${form.on_premise ? form.base_url : 'https://dev.azure.com'}/${form.organization}`;

  const save = async () => {
    setSaving(true); setError(null); setSuccess(false);
    try {
      const payload: Partial<AdoConfigRow> & { pat?: string; pat_expires_at?: string } = {
        organization:   form.organization,
        base_url:       form.on_premise ? form.base_url : 'https://dev.azure.com',
        pipeline_id:    parseInt(form.pipeline_id, 10) || (config?.pipeline_id ?? 1),
        project_name:   form.project_name,
        is_active:      form.is_active,
        pat_expires_at: form.pat_expires_at || undefined,
      };
      if (form.pat) payload.pat = form.pat;
      const { config: updated } = await updateAdminAdoConfig(planCode, payload);
      onSaved(updated);
      setForm(f => ({ ...f, pat: '' }));
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch { setError('Échec de la sauvegarde'); }
    finally   { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await testAdminAdoConfig(planCode);
      setTestResult(res);
      // Refresh the config to get updated health_status
      onSaved({ ...config!, health_status: res.status, last_health_check: new Date().toISOString() });
    } catch { setTestResult({ success: false, message: 'Erreur réseau' }); }
    finally  { setTesting(false); }
  };

  const notConfigured = !config?.organization || config.organization.trim() === '';
  const health = config?.health_status;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
      {/* Header avec indicateur de santé */}
      <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
          <FiSettings size={14} className="text-gray-500" />
          Configuration ADO
          {form.organization && (
            <a
              href={adoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-500 hover:text-indigo-700 transition"
              title={`Ouvrir ${adoUrl}`}
            >
              <FiExternalLink size={13} />
            </a>
          )}
        </h3>
        <div className="flex items-center gap-2">
          {/* Indicateur santé */}
          {health && health !== 'unchecked' && (
            <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
              health === 'ok'    ? 'bg-green-100 text-green-700' :
              health === 'error' ? 'bg-red-100 text-red-700' :
                                   'bg-gray-100 text-gray-500'
            }`}>
              {health === 'ok'    ? <FiWifi size={11} />    :
               health === 'error' ? <FiWifiOff size={11} /> :
               <FiClock size={11} />}
              {health === 'ok' ? 'Connecté' : health === 'error' ? 'Erreur' : health}
              {config?.last_health_check && (
                <span className="opacity-60 ml-1">
                  · {new Date(config.last_health_check).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </span>
          )}
          {/* Bouton Tester */}
          <button
            onClick={testConnection}
            disabled={testing || !config?.has_pat}
            title={!config?.has_pat ? 'Configurez un PAT d\'abord' : 'Tester la connexion ADO'}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-white disabled:opacity-40 transition font-medium"
          >
            {testing
              ? <FiRefreshCw size={11} className="animate-spin" />
              : health === 'ok' ? <FiWifi size={11} className="text-green-500" /> : <FiWifi size={11} />
            }
            Tester
          </button>
        </div>
      </div>

      {/* Résultat du test */}
      {testResult && (
        <div className={`mx-5 mt-3 px-3 py-2 rounded-lg text-xs flex items-center gap-2 border ${
          testResult.success
            ? 'bg-green-50 border-green-200 text-green-700'
            : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          {testResult.success ? <FiCheckCircle size={13} /> : <FiAlertCircle size={13} />}
          {testResult.message}
        </div>
      )}

      <div className="px-5 py-4">
        {/* Banner Bootstrap */}
        {(planCode === 'ENTREPRISE' || planCode === 'BUSINESS') && (
          <div className={`mb-4 p-4 rounded-lg ${
            planCode === 'ENTREPRISE'
              ? 'bg-purple-50 border border-purple-200'
              : 'bg-blue-50 border border-blue-200'
          }`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className={`text-sm font-semibold ${planCode === 'ENTREPRISE' ? 'text-purple-800' : 'text-blue-800'}`}>
                  {planCode === 'ENTREPRISE' ? 'Enterprise' : 'Business'} — provisioning dédié par tenant
                </p>
                <p className={`text-xs mt-0.5 ${planCode === 'ENTREPRISE' ? 'text-purple-700' : 'text-blue-700'}`}>
                  Étape 1 : <strong>Bootstrap Pipelines</strong> — crée le projet ADO, pousse les 3 YAMLs et les 3 pipelines (Azure/AWS/GCP) dans l'orga {planCode === 'ENTREPRISE' ? 'Enterprise' : 'Business'}.
                  Étape 2 : créez une <strong>Lane</strong> (pool ADO dédié + agents K8s) depuis le menu Lanes.
                </p>
              </div>
              <div className="flex flex-col gap-2 flex-shrink-0">
                <button
                  onClick={handleEntBootstrap}
                  disabled={!config?.has_pat}
                  title={!config?.has_pat ? 'Configurez un PAT ADO d\'abord' : `Bootstrap les pipelines ${planCode}`}
                  className={`flex items-center gap-2 px-3 py-2 text-white rounded-lg text-xs font-medium transition whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${
                    planCode === 'ENTREPRISE'
                      ? 'bg-purple-600 hover:bg-purple-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  <FiPlay size={12} /> Bootstrap Pipelines
                </button>
                <button onClick={() => navigate(planCode === 'ENTREPRISE' ? '/admin/enterprise-lanes' : '/admin/business-lanes')}
                  className={`flex items-center gap-2 px-3 py-2 text-white rounded-lg text-xs font-medium transition whitespace-nowrap ${
                    planCode === 'ENTREPRISE'
                      ? 'bg-purple-500 hover:bg-purple-600'
                      : 'bg-blue-500 hover:bg-blue-600'
                  }`}>
                  <FiZap size={12} /> {planCode === 'ENTREPRISE' ? 'Lanes Ent.' : 'Lanes Bus.'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Bootstrap Enterprise Pipeline (SSE) */}
        {entBootstrapOpen && (planCode === 'ENTREPRISE' || planCode === 'BUSINESS') && (
          <EnterpriseBootstrapPipelineModal
            planCode={planCode as 'ENTREPRISE' | 'BUSINESS'}
            progress={entBootstrapProgress}
            done={entBootstrapDone}
            onClose={handleCloseEntBootstrap}
          />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Organisation ADO avec lien */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Organisation ADO</label>
            <input type="text" value={form.organization} onChange={set('organization')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="GenIaC-Free" />
          </div>

          {/* Project name */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Project name</label>
            <input type="text" value={form.project_name} onChange={set('project_name')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="terraform-executions" />
          </div>

          {/* Pipeline ID */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Pipeline ID (Azure)</label>
            <input type="number" value={form.pipeline_id} onChange={set('pipeline_id')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>

          {/* Base URL — simplifié */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Base URL
              <label className="ml-3 font-normal text-gray-400 cursor-pointer inline-flex items-center gap-1">
                <input type="checkbox" checked={form.on_premise} onChange={set('on_premise')} className="w-3 h-3" />
                On-premise (Azure DevOps Server)
              </label>
            </label>
            {form.on_premise ? (
              <input type="text" value={form.base_url} onChange={set('base_url')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="https://ado.votreentreprise.com" />
            ) : (
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500 font-mono">
                https://dev.azure.com
              </div>
            )}
          </div>
        </div>

        {/* PAT */}
        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Personal Access Token (PAT)
            {config?.has_pat && (
              <span className="ml-2 text-green-600 font-normal text-xs">
                <FiCheck size={11} className="inline" /> configuré — laisser vide pour conserver
              </span>
            )}
            <PatExpiryBadge expiresAt={config?.pat_expires_at ?? null} />
          </label>
          <div className="relative">
            <input type={showPat ? 'text' : 'password'} value={form.pat} onChange={set('pat')}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder={config?.has_pat ? '••••••••••••••••' : 'Entrez le PAT Azure DevOps'} />
            <button type="button" onClick={() => setShowPat(s => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              {showPat ? <FiEyeOff size={16} /> : <FiEye size={16} />}
            </button>
          </div>
        </div>

        {/* PAT expiration */}
        <div className="mt-3">
          <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
            <FiClock size={11} /> Date d'expiration du PAT
            {patDaysLeft(form.pat_expires_at || null) !== null && patDaysLeft(form.pat_expires_at || null)! <= 30 && (
              <FiAlertTriangle size={11} className="text-orange-500 ml-1" />
            )}
          </label>
          <input type="date" value={form.pat_expires_at} onChange={set('pat_expires_at')}
            className="w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>

        {/* Actif */}
        <div className="flex items-center gap-2 mt-3">
          <input type="checkbox" id={`active-${planCode}`} checked={form.is_active} onChange={set('is_active')}
            className="h-4 w-4 text-indigo-600 border-gray-300 rounded" />
          <label htmlFor={`active-${planCode}`} className="text-sm text-gray-700">Configuration active</label>
        </div>

        {error && <p className="mt-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
          {success
            ? <span className="text-sm text-green-600 flex items-center gap-1"><FiCheck size={14} /> Sauvegardé</span>
            : <span />
          }
          <button onClick={save} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg transition disabled:opacity-50">
            {saving ? <FiRefreshCw size={14} className="animate-spin" /> : <FiSave size={14} />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Section Factory Projects ─────────────────────────────────────────────────

interface FactorySectionProps {
  planCode:  PlanCode;
  projects:  FactoryProject[];
  onRefresh: () => void;
}

// ── Bootstrap Progress Modal ─────────────────────────────────────────────────

const STEP_ORDER = ['project', 'agentPool', 'queue', 'variableGroup', 'yaml', 'pipeline', 'pipelineVars', 'pipelinePermission', 'agents'] as const;

const STEP_LABELS: Record<string, string> = {
  project:            'Projet ADO',
  agentPool:          'Agent Pool',
  queue:              'Liaison queue',
  variableGroup:      'Variable Group',
  yaml:               'YAML pipeline',
  pipeline:           'Création pipeline',
  pipelineVars:       'Variables ARM',
  pipelinePermission: 'Permission pipeline',
  agents:             'Agents connectés',
};

interface BootstrapProgressModalProps {
  fp:       FactoryProject;
  progress: Map<string, BootstrapProgressEvent>;
  done:     boolean;
  onClose:  () => void;
}

const BootstrapProgressModal: React.FC<BootstrapProgressModalProps> = ({ fp, progress, done, onClose }) => {
  const doneEvent  = progress.get('done');
  const errorEvent = progress.get('error');

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            <FiZap size={15} className="text-indigo-600" />
            Bootstrap — <span className="font-mono text-indigo-700">{fp.name}</span>
          </h2>
          {done && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
              <FiX size={16} />
            </button>
          )}
        </div>

        {/* Steps */}
        <div className="px-5 py-4 space-y-3">
          {STEP_ORDER.map(step => {
            const event  = progress.get(step);
            const status = event?.status ?? 'pending';

            // Hide variableGroup if never emitted
            if (!event && step === 'variableGroup') return null;

            return (
              <div key={step} className="flex items-start gap-3">
                <div className="mt-0.5 flex-shrink-0 w-4">
                  {status === 'running'  && <FiRefreshCw  size={14} className="animate-spin text-indigo-500" />}
                  {status === 'success'  && <FiCheckCircle size={14} className="text-green-500" />}
                  {status === 'warning'  && <FiAlertCircle size={14} className="text-amber-500" />}
                  {status === 'error'    && <FiAlertCircle size={14} className="text-red-500" />}
                  {status === 'pending'  && <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 mt-0.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${
                    status === 'error'   ? 'text-red-700' :
                    status === 'warning' ? 'text-amber-700' :
                    status === 'success' ? 'text-green-700' :
                    status === 'running' ? 'text-indigo-700' :
                    'text-gray-400'
                  }`}>
                    {STEP_LABELS[step]}
                  </p>
                  {event?.message && (
                    <p className="text-xs text-gray-500 truncate mt-0.5">{event.message}</p>
                  )}
                  {event?.detail && (
                    <p className="text-xs text-gray-400 font-mono truncate">{event.detail}</p>
                  )}
                </div>
              </div>
            );
          })}

          {/* Final result banner */}
          {doneEvent && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <FiCheck size={13} className="text-green-600 flex-shrink-0" />
              <span className="text-xs text-green-700">{doneEvent.message}</span>
            </div>
          )}
          {errorEvent && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <FiAlertCircle size={13} className="text-red-600 flex-shrink-0" />
              <span className="text-xs text-red-700">{errorEvent.message}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex justify-end">
          <button
            onClick={onClose}
            disabled={!done}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {done ? 'Fermer' : 'Bootstrap en cours…'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Enterprise Bootstrap Pipeline Modal ──────────────────────────────────────

const ENT_STEP_ORDER = ['project', 'yaml', 'pipeline', 'pipelineVars', 'done'] as const;
const ENT_STEP_LABELS: Record<string, string> = {
  project:     'Projet ADO',
  yaml:        'YAML pipeline (3 providers)',
  pipeline:    'Création pipelines (Azure/AWS/GCP)',
  pipelineVars:'Variables de pipeline',
  done:        'Terminé',
};

interface EnterpriseBootstrapModalProps {
  planCode: 'ENTREPRISE' | 'BUSINESS';
  progress: Map<string, BootstrapEnterprisePipelineEvent>;
  done:     boolean;
  onClose:  () => void;
}

const EnterpriseBootstrapPipelineModal: React.FC<EnterpriseBootstrapModalProps> = ({ planCode, progress, done, onClose }) => {
  const doneEvent  = progress.get('done');
  const errorEvent = progress.get('error');
  const isEnt      = planCode === 'ENTREPRISE';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
        {/* Header */}
        <div className={`px-5 py-4 border-b border-gray-100 flex items-center justify-between`}>
          <h2 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            <FiZap size={15} className={isEnt ? 'text-purple-600' : 'text-blue-600'} />
            Bootstrap Pipelines — <span className={`font-mono ${isEnt ? 'text-purple-700' : 'text-blue-700'}`}>{planCode}</span>
          </h2>
          {done && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
              <FiX size={16} />
            </button>
          )}
        </div>

        {/* Steps */}
        <div className="px-5 py-4 space-y-3">
          {ENT_STEP_ORDER.filter(s => s !== 'done').map(step => {
            const event  = progress.get(step);
            const status = event?.status ?? 'pending';
            return (
              <div key={step} className="flex items-start gap-3">
                <div className="mt-0.5 flex-shrink-0 w-4">
                  {status === 'running'  && <FiRefreshCw  size={14} className="animate-spin text-indigo-500" />}
                  {status === 'success'  && <FiCheckCircle size={14} className="text-green-500" />}
                  {status === 'warning'  && <FiAlertCircle size={14} className="text-amber-500" />}
                  {status === 'error'    && <FiAlertCircle size={14} className="text-red-500" />}
                  {status === 'pending'  && <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 mt-0.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${
                    status === 'error'   ? 'text-red-700' :
                    status === 'warning' ? 'text-amber-700' :
                    status === 'success' ? 'text-green-700' :
                    status === 'running' ? 'text-indigo-700' :
                    'text-gray-400'
                  }`}>
                    {ENT_STEP_LABELS[step] ?? step}
                  </p>
                  {event?.message && (
                    <p className="text-xs text-gray-500 truncate mt-0.5">{event.message}</p>
                  )}
                  {event?.detail && (
                    <p className="text-xs text-gray-400 font-mono truncate">{event.detail}</p>
                  )}
                </div>
              </div>
            );
          })}

          {/* Afficher toutes les autres étapes inconnues reçues */}
          {Array.from(progress.entries())
            .filter(([step]) => !([...ENT_STEP_ORDER, 'start', 'error'] as string[]).includes(step))
            .map(([step, event]) => (
              <div key={step} className="flex items-start gap-3">
                <div className="mt-0.5 flex-shrink-0 w-4">
                  {event.status === 'running'  && <FiRefreshCw  size={14} className="animate-spin text-indigo-500" />}
                  {event.status === 'success'  && <FiCheckCircle size={14} className="text-green-500" />}
                  {event.status === 'warning'  && <FiAlertCircle size={14} className="text-amber-500" />}
                  {event.status === 'error'    && <FiAlertCircle size={14} className="text-red-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-600">{STEP_LABELS[step] ?? step}</p>
                  {event.message && <p className="text-xs text-gray-500 truncate mt-0.5">{event.message}</p>}
                </div>
              </div>
            ))
          }

          {doneEvent && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <FiCheck size={13} className="text-green-600 flex-shrink-0" />
              <span className="text-xs text-green-700">{doneEvent.message}</span>
            </div>
          )}
          {errorEvent && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <FiAlertCircle size={13} className="text-red-600 flex-shrink-0" />
              <span className="text-xs text-red-700">{errorEvent.message}</span>
            </div>
          )}
        </div>

        <div className="px-5 pb-5 flex justify-end">
          <button
            onClick={onClose}
            disabled={!done}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {done ? 'Fermer' : 'Bootstrap en cours…'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Factory Section ───────────────────────────────────────────────────────────

const FactorySection: React.FC<FactorySectionProps> = ({ planCode, projects, onRefresh }) => {
  const [modal,   setModal]   = useState<'create' | 'edit' | null>(null);
  const [editing, setEditing] = useState<FactoryProject | null>(null);
  const [err,     setErr]     = useState<string | null>(null);

  // SSE bootstrap progress modal
  const [bootstrapModalFp, setBootstrapModalFp] = useState<FactoryProject | null>(null);
  const [bootstrapProgress, setBootstrapProgress] = useState<Map<string, BootstrapProgressEvent>>(new Map());
  const [bootstrapDone, setBootstrapDone]   = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  const totalSlots = projects.reduce((s, p) => s + (p.is_active ? p.max_concurrency : 0), 0);
  const usedSlots  = projects.reduce((s, p) => s + (p.is_active ? p.active_runs : 0), 0);
  const pct        = totalSlots > 0 ? Math.round((usedSlots / totalSlots) * 100) : 0;

  const handleToggle = async (fp: FactoryProject) => {
    try {
      await updateFactoryProject(fp.id, { is_active: !fp.is_active });
      onRefresh();
    } catch {
      setErr(`Impossible de ${fp.is_active ? 'désactiver' : 'activer'} le projet`);
    }
  };

  const handleDelete = async (fp: FactoryProject) => {
    if (!window.confirm(`Supprimer "${fp.name}" ? Cette action est irréversible.`)) return;
    try {
      await deleteFactoryProject(fp.id);
      onRefresh();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Erreur lors de la suppression');
    }
  };

  const handleCloseBootstrapModal = () => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    const wasDone = bootstrapDone;
    setBootstrapModalFp(null);
    setBootstrapProgress(new Map());
    setBootstrapDone(false);
    // Refresh seulement après fermeture pour ne pas unmount le composant en cours de bootstrap
    if (wasDone) onRefresh();
  };

  const handleBootstrap = (fp: FactoryProject) => {
    setBootstrapModalFp(fp);
    setBootstrapProgress(new Map());
    setBootstrapDone(false);
    setErr(null);

    const cleanup = streamBootstrapFactoryProject(fp.id, (event) => {
      setBootstrapProgress(prev => new Map(prev).set(event.step, event));
      if (event.step === 'done' || event.step === 'error') {
        setBootstrapDone(true);
        // NE PAS appeler onRefresh() ici : setLoading(true) unmount FactorySection et ferme la modale
      }
    });
    cleanupRef.current = cleanup;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
            <FiZap size={14} className="text-gray-500" />
            Factory Projects
          </h3>
          {totalSlots > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-24 bg-gray-200 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full transition-all ${pct > 80 ? 'bg-red-500' : pct > 50 ? 'bg-amber-400' : 'bg-green-500'}`}
                  style={{ width: `${Math.max(pct, pct > 0 ? 4 : 0)}%` }}
                />
              </div>
              <span className={`text-xs font-medium ${pct > 80 ? 'text-red-600' : pct > 50 ? 'text-amber-600' : 'text-gray-500'}`}>
                {usedSlots}/{totalSlots} slots
              </span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                pct === 0    ? 'bg-green-50 text-green-600' :
                pct > 80     ? 'bg-red-100 text-red-600' :
                pct > 50     ? 'bg-amber-100 text-amber-600' :
                               'bg-blue-50 text-blue-600'
              }`}>
                {pct === 0 ? 'Disponible' : pct > 80 ? 'Saturé' : pct > 50 ? 'Occupé' : `${pct}%`}
              </span>
            </div>
          )}
        </div>
        {planCode === 'ENTREPRISE' ? (
          <button disabled
            title="Pour Enterprise, utilisez Lanes Ent. (menu Infrastructure → Lanes Ent.)"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-200 text-gray-400 rounded-lg text-xs font-medium cursor-not-allowed opacity-60">
            <FiPlusCircle size={12} /> Nouveau projet
          </button>
        ) : (
          <button
            onClick={() => { setEditing(null); setModal('create'); }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 transition"
          >
            <FiPlusCircle size={12} /> Nouveau projet
          </button>
        )}
      </div>

      {err && (
        <div className={`mx-5 mt-3 p-2 rounded-lg text-xs flex items-center gap-2 border ${
          err.startsWith('✅')
            ? 'bg-green-50 border-green-200 text-green-700'
            : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          {err.startsWith('✅') ? <FiCheckCircle size={12} /> : <FiAlertCircle size={12} />} {err}
        </div>
      )}

      {projects.length === 0 ? (
        <div className="p-8 text-center">
          <p className="text-gray-500 text-sm">Aucun factory project pour ce plan.</p>
          <p className="text-gray-400 text-xs mt-1">
            Créez-en un ou vérifiez que la migration <code className="bg-gray-100 px-1 rounded">023</code> a été exécutée.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs">Nom</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs">Projet ADO</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs">Pipelines (Azure/AWS/GCP)</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs">Charge</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs">Statut</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {projects.map(fp => {
                const fpPct = fp.max_concurrency > 0
                  ? Math.round((fp.active_runs / fp.max_concurrency) * 100)
                  : 0;
                return (
                  <tr key={fp.id} className={`hover:bg-gray-50 transition ${!fp.is_active ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3 font-mono text-xs text-gray-800">{fp.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{fp.ado_project}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      <div className="space-y-0.5">
                        <div>
                          <span className="text-gray-400">AZ:</span>{' '}
                          {fp.pipeline_id
                            ? <span className="font-mono">{fp.pipeline_id}</span>
                            : <span className="text-gray-300 italic">hérite</span>}
                        </div>
                        <div>
                          <span className={fp.pipeline_id_aws ? 'text-gray-400' : 'text-amber-500 font-semibold'}>AWS:</span>{' '}
                          {fp.pipeline_id_aws
                            ? <span className="font-mono">{fp.pipeline_id_aws}</span>
                            : <span className="text-amber-500 italic">⚠ Re-bootstrap requis</span>}
                        </div>
                        <div>
                          <span className={fp.pipeline_id_gcp ? 'text-gray-400' : 'text-amber-500 font-semibold'}>GCP:</span>{' '}
                          {fp.pipeline_id_gcp
                            ? <span className="font-mono">{fp.pipeline_id_gcp}</span>
                            : <span className="text-amber-500 italic">⚠ Re-bootstrap requis</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${fpPct > 80 ? 'bg-red-500' : fpPct > 50 ? 'bg-amber-400' : 'bg-green-500'}`}
                            style={{ width: `${Math.max(fpPct, fpPct > 0 ? 6 : 0)}%` }}
                          />
                        </div>
                        <span className={`text-xs font-medium ${fpPct > 80 ? 'text-red-600' : fpPct > 50 ? 'text-amber-600' : 'text-gray-500'}`}>
                          {fp.active_runs}/{fp.max_concurrency}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {fp.is_active
                        ? <span className="flex items-center gap-1 text-green-600 text-xs"><FiCheckCircle size={12} /> Actif</span>
                        : <span className="flex items-center gap-1 text-gray-400 text-xs"><FiAlertCircle size={12} /> Désactivé</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleToggle(fp)}
                          title={fp.is_active ? 'Désactiver' : 'Activer'}
                          className="text-gray-500 hover:text-indigo-600 transition"
                        >
                          {fp.is_active
                            ? <FiToggleRight size={16} className="text-green-500" />
                            : <FiToggleLeft size={16} />}
                        </button>
                        {planCode === 'ENTREPRISE' ? (
                          <button disabled
                            title="Bootstrap non disponible pour Enterprise — utilisez Lanes Ent."
                            className="text-gray-300 cursor-not-allowed opacity-40">
                            <FiPlay size={14} />
                          </button>
                        ) : (
                          <button onClick={() => handleBootstrap(fp)}
                            disabled={bootstrapModalFp?.id === fp.id}
                            title="Re-bootstrapper (idempotent)"
                            className="text-gray-500 hover:text-indigo-600 transition disabled:opacity-30"
                          >
                            <FiPlay size={14} />
                          </button>
                        )}
                        <button onClick={() => { setEditing(fp); setModal('edit'); }}
                          title="Modifier"
                          className="text-gray-500 hover:text-indigo-600 transition"
                        >
                          <FiEdit2 size={14} />
                        </button>
                        <button onClick={() => handleDelete(fp)}
                          title="Supprimer"
                          disabled={fp.active_runs > 0}
                          className="text-gray-500 hover:text-red-600 transition disabled:opacity-30"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <FactoryProjectModal
          planCode={planCode}
          initial={modal === 'edit' && editing ? editing : undefined}
          onClose={() => { setModal(null); setEditing(null); }}
          onSave={() => { setModal(null); setEditing(null); onRefresh(); }}
        />
      )}

      {bootstrapModalFp && (
        <BootstrapProgressModal
          fp={bootstrapModalFp}
          progress={bootstrapProgress}
          done={bootstrapDone}
          onClose={handleCloseBootstrapModal}
        />
      )}
    </div>
  );
};

// ── Page principale ──────────────────────────────────────────────────────────

const AdminInfrastructure: React.FC = () => {
  const navigate = useNavigate();
  const [configs,  setConfigs]  = useState<AdoConfigRow[]>([]);
  const [projects, setProjects] = useState<FactoryProject[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);

  const [activeTab,     setActiveTab]     = useState<PlanCode>('FREE');
  const [bootstrapPlan, setBootstrapPlan] = useState<PlanCode | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ configs: c }, { factory_projects: fps }] = await Promise.all([
        getAdminAdoConfig(),
        getFactoryProjects(),
      ]);
      setConfigs(c);
      setProjects(fps);
    } catch {
      setError('Impossible de charger les données d\'infrastructure');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const configByPlan   = Object.fromEntries(configs.map(c => [c.plan_code, c]));
  const projectsByPlan = PLANS.reduce((acc, p) => {
    acc[p] = projects.filter(fp => fp.plan_code === p);
    return acc;
  }, {} as Record<PlanCode, FactoryProject[]>);

  // Indicateur par onglet : slots actifs
  const tabStats = PLANS.map(p => {
    const fps       = projectsByPlan[p].filter(fp => fp.is_active);
    const slots     = fps.reduce((s, fp) => s + fp.max_concurrency, 0);
    const used      = fps.reduce((s, fp) => s + fp.active_runs, 0);
    const configured = !!configByPlan[p]?.is_active;
    return { plan: p, slots, used, configured };
  });

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiServer className="text-slate-600" />
            Infrastructure ADO
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Configuration, factory projects et capacité d'exécution par plan
          </p>
        </div>
        <button onClick={load}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition"
        >
          <FiRefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <FiAlertCircle size={14} /> {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-0 mb-5 border-b border-gray-200">
        {tabStats.map(({ plan, slots, used, configured }) => {
          const isActive = activeTab === plan;
          return (
            <button
              key={plan}
              onClick={() => setActiveTab(plan as PlanCode)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px flex items-center gap-2 ${
                isActive
                  ? 'border-indigo-600 text-indigo-600 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <PlanBadge plan={plan} />
              {plan === 'BUSINESS' ? (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">BYOO</span>
              ) : configured ? (
                <span className="text-xs text-gray-400 font-normal">
                  {slots > 0 ? `${used}/${slots}` : '0 slots'}
                </span>
              ) : (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                  Non configuré
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Contenu de l'onglet actif */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400">Chargement…</div>
      ) : activeTab === 'BUSINESS' ? (
        /* ── Business (BYOO) : la config ADO est gérée par le client, pas ici ── */
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-xl shrink-0">
              <FiZap className="text-blue-600" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 text-lg mb-1">Plan Business — BYOO</h3>
              <p className="text-blue-800 text-sm mb-4">
                La configuration Azure DevOps (organisation, PAT, pipeline IDs) est gérée <strong>directement par le client</strong> dans son interface GenIaC (Settings → BYOO Azure DevOps).
                Il n'y a pas de config ADO centralisée à maintenir ici pour ce plan.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-white rounded-lg border border-blue-200 p-4">
                  <h4 className="font-medium text-blue-900 text-sm mb-2">Étape 1 — Bootstrap client</h4>
                  <p className="text-xs text-blue-700 mb-3">
                    Le client configure son ADO et clique "Activer". Les pipelines sont créés dans son org ADO.
                    L'admin peut surveiller via le dashboard Business Orgs.
                  </p>
                  <button
                    onClick={() => navigate('/admin/business-lanes')}
                    className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition"
                  >
                    <FiZap size={12} /> Gérer les Business Lanes
                  </button>
                </div>
                <div className="bg-white rounded-lg border border-blue-200 p-4">
                  <h4 className="font-medium text-blue-900 text-sm mb-2">Étape 2 — Déploiement agents</h4>
                  <p className="text-xs text-blue-700 mb-3">
                    Après bootstrap, l'admin clique "Activer" sur la lane pour déployer les agents GenIaC
                    connectés à l'org ADO du client via son PAT chiffré.
                  </p>
                  <button
                    onClick={() => navigate('/admin/business-lanes')}
                    className="flex items-center gap-2 px-3 py-1.5 border border-blue-300 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-50 transition"
                  >
                    <FiServer size={12} /> Voir les agents
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <ConfigSection
            config={configByPlan[activeTab]}
            planCode={activeTab}
            onSaved={updated => setConfigs(cs => cs.map(c => c.plan_code === updated.plan_code ? updated : c))}
            onBootstrap={() => setBootstrapPlan(activeTab)}
          />
          {activeTab !== 'ENTREPRISE' && (
            <FactorySection
              planCode={activeTab}
              projects={projectsByPlan[activeTab]}
              onRefresh={load}
            />
          )}
        </div>
      )}

      {/* Modal Bootstrap */}
      {bootstrapPlan && (
        <BootstrapModal
          planCode={bootstrapPlan}
          onClose={() => setBootstrapPlan(null)}
          onSuccess={() => { setBootstrapPlan(null); load(); }}
        />
      )}
    </div>
  );
};

export default AdminInfrastructure;
